import { useArtifactViewCtx } from "@/components/artifacts/view/ArtifactViewContext.tsx";
import { Button } from "@/components/ui/button.tsx";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select.tsx";
import { errorToast } from "@/components/ui/toast.tsx";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip.tsx";
import type { ArtifactComponent } from "@/types/artifacts-view.ts";
import { Info } from "lucide-react";
import { useState } from "react";

type SelectComponentData = Extract<ArtifactComponent, { type: "select" }>;

export const SelectBlock = ({ component }: { component: SelectComponentData }) => {
  const { isReadOnly, patchOne } = useArtifactViewCtx();
  const canEdit = !isReadOnly && component.editable;
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const onValueChange = async (v: string) => {
    try {
      await patchOne(component.editPath, v);
    } catch (error) {
      console.error(error);
      errorToast("Error updating field");
    }
  };

  const dropdown = (
    <Select value={component.value} disabled={!canEdit} onValueChange={onValueChange}>
      <SelectTrigger className="h-7 text-xs w-auto gap-1.5 rounded-md">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {component.options.map((opt) => (
          <SelectItem key={opt.value} value={opt.value}>
            {opt.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  // Card style: bordered row with the help text shown inline, matching the
  // permission-toggle cards (used for routine's "Default From").
  if (component.style === "card") {
    return (
      <div className="flex w-full items-center justify-between gap-3 rounded-lg border p-3">
        <div className="text-left">
          {component.label ? <div className="text-sm font-medium">{component.label}</div> : null}
          {component.helpText ? (
            <div className="mt-0.5 text-xs text-muted-foreground">{component.helpText}</div>
          ) : null}
        </div>
        {dropdown}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 mt-1.5">
      {component.label ? (
        <span className="text-xs text-muted-foreground w-28 shrink-0">{component.label}</span>
      ) : null}
      {dropdown}
      {component.helpText ? (
        <Tooltip open={tooltipOpen} onOpenChange={setTooltipOpen}>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              data-statsig-name="artifact-view-select-info"
              onClick={() => setTooltipOpen((o) => !o)}
              className="h-4 w-4 p-0"
            >
              <Info className="h-3 w-3 text-muted-foreground/60" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-64 text-xs">
            {component.helpText}
          </TooltipContent>
        </Tooltip>
      ) : null}
    </div>
  );
};
